import { LitElement, html, css, nothing, type TemplateResult } from 'lit';
import { customElement, property, state, queryAll } from 'lit/decorators.js';
import { api, HttpError } from '../api/client';
import type { Deck, DeckAnalytics } from '../api/types';
import { renderDeck } from '../lib/render-deck';
import { navigate } from '../lib/router-state';
import { setDeckTitle } from '../lib/page-title';
import {
  toSlideRows,
  toViewerRows,
  toOverview,
  isAnalyticsEmpty,
  type SlideRow,
} from '../lib/analytics-format';

/**
 * Format an ISO timestamp as "12 May 2026" — falls back gracefully.
 * Local copy (the deck-card helper is private) so the page can show the
 * "last seen" column on the recent-viewers list.
 */
function fmtDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Full-page owner-facing deck analytics (sharing v2 — promoted out of the
 * inline deck-card panel).
 *
 * On mount (or when `deck-id` changes) it fetches the deck AND its analytics
 * roll-up in parallel, then renders:
 *
 *   - a back link to the deck (and the library);
 *   - an OVERVIEW card (total opens, unique viewers, recent-viewer count,
 *     plus a recent-viewers list via the analytics-format helpers);
 *   - a SLIDE-BY-SLIDE list driven by `toSlideRows` so ordering + bar widths
 *     match the inline panel exactly.
 *
 * PER-SLIDE PREVIEW — two cases, detected via `deck.model != null`:
 *
 *   Option A (model present): each slide row renders a small scaled iframe
 *     whose srcdoc is `renderDeck({ meta, slides: [model.slides[i]] })` — a
 *     one-slide self-contained doc that reproduces that slide accurately. The
 *     iframe uses the exact deck-card thumbnail recipe (1920x1080 scaled into
 *     a 16/9 box, sandbox + allow, pointer-events none, theme pushed via
 *     `deck:theme` postMessage on load + on `themechange`).
 *
 *   Option B (model null — legacy/raw-html decks): we do NOT attempt N
 *     per-slide previews (there is no model to slice). Instead one full-deck
 *     iframe (srcdoc = deck.html, `deck:mode` thumb on load to hide the rail)
 *     renders at the top, and each slide row is a plain row with no per-row
 *     thumbnail.
 *
 * Auth: the shell (<deck-app>) gates every route that is not /login or
 * /share/*, so this route is owner-protected with no extra guard. A
 * cross-owner / missing deck surfaces as an HttpError 404, which we render
 * as a friendly "Not found or not yours" message with a link home.
 *
 * British spelling throughout. No emojis.
 */
@customElement('deck-analytics-page')
export class DeckAnalyticsPage extends LitElement {
  @property({ attribute: 'deck-id' }) deckId = '';
  @state() private deck: Deck | null = null;
  @state() private analytics: DeckAnalytics | null = null;
  @state() private loading = true;
  /** Generic error string for non-404 failures. */
  @state() private error: string | null = null;
  /** True when the deck is missing or owned by someone else (HTTP 404). */
  @state() private notFound = false;
  @queryAll('iframe.preview-iframe') private iframeEls!: NodeListOf<HTMLIFrameElement>;

  static styles = css`
    :host {
      display: block;
    }
    .page {
      max-width: 1100px;
      margin: 0 auto;
      padding: 32px;
    }
    .back-row {
      display: flex;
      gap: 16px;
      align-items: center;
      margin-bottom: 20px;
      font-size: 0.85rem;
    }
    .back-row a {
      color: var(--text-secondary);
      text-decoration: none;
      border-bottom: 1px solid transparent;
      cursor: pointer;
      transition: color 120ms, border-color 120ms;
    }
    .back-row a:hover {
      color: var(--text-primary);
      border-bottom-color: var(--border-default);
    }
    h1 {
      font-family: var(--font-editorial, Georgia, serif);
      font-weight: 400;
      font-size: 2rem;
      letter-spacing: -0.01em;
      margin: 0 0 4px;
      color: var(--text-primary);
    }
    .deck-sub {
      color: var(--text-secondary);
      margin: 0 0 28px;
      font-size: 0.95rem;
    }
    .state {
      padding: 64px 32px;
      text-align: center;
      color: var(--text-secondary);
    }
    .state.error { color: var(--danger); }
    .state a {
      color: var(--text-primary);
      text-decoration: underline;
      text-underline-offset: 2px;
      cursor: pointer;
    }

    /* ─── Overview card ───────────────────────────────────────────────── */
    .card {
      background: var(--surface);
      border: 1px solid var(--border-subtle);
      border-radius: 12px;
      padding: 22px;
      margin-bottom: 28px;
    }
    .overview-stats {
      display: flex;
      gap: 40px;
      flex-wrap: wrap;
    }
    .stat {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .stat .num {
      font-family: var(--font-editorial, Georgia, serif);
      font-size: 2rem;
      font-weight: 500;
      color: var(--text-primary);
      line-height: 1.1;
    }
    .stat .lbl {
      font-size: 0.72rem;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: var(--text-tertiary);
    }
    .section-title {
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--text-tertiary);
      margin: 24px 0 10px;
    }
    .viewers {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .viewers li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      font-size: 0.85rem;
      padding: 8px 10px;
      border: 1px solid var(--border-subtle);
      border-radius: 6px;
      background: var(--canvas);
    }
    .viewers .v-name {
      color: var(--text-primary);
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1 1 auto;
    }
    .viewers .v-meta {
      flex: 0 0 auto;
      color: var(--text-tertiary);
      font-variant-numeric: tabular-nums;
      text-align: right;
    }
    .empty {
      font-size: 0.85rem;
      color: var(--text-tertiary);
      padding: 6px 0;
    }

    /* ─── Full-deck fallback iframe (no model) ────────────────────────── */
    .full-preview {
      position: relative;
      width: 100%;
      aspect-ratio: 16 / 9;
      background: var(--canvas);
      overflow: hidden;
      border: 1px solid var(--border-subtle);
      border-radius: 12px;
      margin-bottom: 28px;
    }
    .full-preview iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 1920px;
      height: 1080px;
      border: 0;
      transform-origin: top left;
      pointer-events: none;
      background: var(--canvas);
    }

    /* ─── Slide-by-slide list ─────────────────────────────────────────── */
    .slides {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .slide-row {
      display: grid;
      grid-template-columns: 160px 1fr 56px;
      gap: 16px;
      align-items: center;
      padding: 12px;
      border: 1px solid var(--border-subtle);
      border-radius: 10px;
      background: var(--surface);
    }
    /* Plain row (no model) — no thumbnail column. */
    .slide-row.no-thumb {
      grid-template-columns: 1fr 56px;
    }
    /* Per-row preview thumbnail — same recipe as the deck-card cover preview:
       a 16/9 box clipping a 1920x1080 iframe scaled down via transform. */
    .thumb {
      position: relative;
      width: 160px;
      aspect-ratio: 16 / 9;
      background: var(--canvas);
      overflow: hidden;
      border: 1px solid var(--border-subtle);
      border-radius: 6px;
      flex: 0 0 auto;
    }
    .thumb iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 1920px;
      height: 1080px;
      border: 0;
      transform-origin: top left;
      pointer-events: none;
      background: var(--canvas);
    }
    .slide-meta {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 0;
    }
    .slide-name {
      font-size: 0.95rem;
      color: var(--text-primary);
      font-weight: 500;
    }
    .slide-label {
      color: var(--text-secondary);
      font-weight: 400;
    }
    .bar-track {
      height: 8px;
      background: var(--surface-hover);
      border-radius: 999px;
      overflow: hidden;
    }
    .bar-fill {
      height: 100%;
      background: var(--ink);
      border-radius: 999px;
    }
    .views {
      text-align: right;
      color: var(--text-primary);
      font-variant-numeric: tabular-nums;
      font-size: 0.9rem;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('themechange', this.onThemeChange);
    window.addEventListener('resize', this.onResize);
    void this.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('themechange', this.onThemeChange);
    window.removeEventListener('resize', this.onResize);
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('deckId')) {
      void this.load();
    }
    // Re-fit every preview iframe after a render in case the layout changed
    // (e.g. the slide list mounted). Each iframe also self-fits on its own
    // @load, so this is belt-and-braces for size-after-data-arrives.
    this.fitAll();
  }

  private currentTheme(): 'light' | 'dark' {
    const t = document.documentElement.getAttribute('data-theme');
    return t === 'dark' ? 'dark' : 'light';
  }

  private onThemeChange = () => {
    // Re-theme every live preview iframe (same pattern as deck-card).
    for (const iframe of Array.from(this.iframeEls ?? [])) {
      this.postTo(iframe, { type: 'deck:theme', theme: this.currentTheme() });
    }
  };

  private onResize = () => this.fitAll();

  /** Fired by each preview iframe's own @load. `full` previews also get the
   *  thumb-mode message so the deck rail/chrome is hidden. */
  private onIframeLoad(e: Event, full: boolean) {
    const iframe = e.currentTarget as HTMLIFrameElement;
    if (full) {
      this.postTo(iframe, { type: 'deck:mode', mode: 'thumb' });
    }
    this.postTo(iframe, { type: 'deck:theme', theme: this.currentTheme() });
    this.fit(iframe);
  }

  private postTo(iframe: HTMLIFrameElement | undefined, msg: unknown) {
    try {
      iframe?.contentWindow?.postMessage(msg, '*');
    } catch (e) { /* defensive */ }
  }

  private fitAll() {
    for (const iframe of Array.from(this.iframeEls ?? [])) {
      this.fit(iframe);
    }
  }

  private fit(iframe: HTMLIFrameElement) {
    const container = iframe.parentElement;
    if (!container) return;
    const containerWidth = container.clientWidth;
    if (!containerWidth) return;
    const scale = containerWidth / 1920;
    iframe.style.transform = `scale(${scale})`;
  }

  private async load() {
    if (!this.deckId) {
      this.loading = false;
      this.error = 'Missing deck id';
      return;
    }
    this.loading = true;
    this.error = null;
    this.notFound = false;
    this.deck = null;
    this.analytics = null;
    setDeckTitle(undefined, false); // plain "Folio" until the title is known
    try {
      const [deck, analytics] = await Promise.all([
        api.getDeck(this.deckId),
        api.getDeckAnalytics(this.deckId),
      ]);
      this.deck = deck;
      this.analytics = analytics;
      setDeckTitle(deck.title);
    } catch (err) {
      if (err instanceof HttpError && err.status === 404) {
        this.notFound = true;
      } else {
        this.error = err instanceof Error ? err.message : 'Failed to load analytics';
      }
    } finally {
      this.loading = false;
    }
  }

  private goHref(href: string, e: Event) {
    e.preventDefault();
    navigate(href);
  }

  /** True when this deck carries an editable model — drives per-slide previews. */
  private get hasModel(): boolean {
    return this.deck?.model != null;
  }

  /** Total slide count for the "N / total" viewer progress label. Prefer the
   *  model's slide count; fall back to the analytics per-slide length. */
  private get totalSlides(): number {
    if (this.deck?.model) return this.deck.model.slides.length;
    return this.analytics?.perSlide?.length ?? 0;
  }

  /** Compile a one-slide self-contained doc for a given deck-stage slide index.
   *  Returns null when the index is out of the model's bounds (defensive — a
   *  stale analytics row could reference a slide that no longer exists). */
  private slideSrcdoc(slideIndex: number): string | null {
    const model = this.deck?.model;
    if (!model) return null;
    if (slideIndex < 0 || slideIndex >= model.slides.length) return null;
    return renderDeck({ meta: model.meta, slides: [model.slides[slideIndex]] });
  }

  /** The human label for a slide row — the model's slide `label` if present. */
  private slideLabel(row: SlideRow): string | undefined {
    const model = this.deck?.model;
    if (!model) return undefined;
    if (row.slideIndex < 0 || row.slideIndex >= model.slides.length) return undefined;
    return model.slides[row.slideIndex].label;
  }

  render(): TemplateResult {
    if (this.loading) {
      return html`<div class="state">Loading analytics…</div>`;
    }
    if (this.notFound) {
      return html`
        <div class="state">
          <p>Not found, or this deck isn't yours.</p>
          <a href="/" @click=${(e: Event) => this.goHref('/', e)}>Back to your decks</a>
        </div>
      `;
    }
    if (this.error) {
      return html`<div class="state error">Couldn't load analytics — ${this.error}</div>`;
    }
    const deck = this.deck;
    if (!deck) {
      return html`<div class="state">No deck loaded.</div>`;
    }
    const viewerHref = `/decks/${encodeURIComponent(deck.id)}`;
    return html`
      <div class="page">
        <div class="back-row">
          <a href=${viewerHref} @click=${(e: Event) => this.goHref(viewerHref, e)}>← Back to deck</a>
          <a href="/" @click=${(e: Event) => this.goHref('/', e)}>All decks</a>
        </div>
        <h1>${deck.title || 'Untitled deck'} — analytics</h1>
        ${deck.subtitle ? html`<p class="deck-sub">${deck.subtitle}</p>` : nothing}
        ${this.renderBody()}
      </div>
    `;
  }

  private renderBody(): TemplateResult {
    if (isAnalyticsEmpty(this.analytics)) {
      // Still show the full-deck preview in the no-model case so the owner sees
      // their deck; otherwise just the empty notice.
      return html`
        ${!this.hasModel ? this.renderFullPreview() : nothing}
        <div class="card">
          <div class="empty">
            No views yet. Analytics appear once someone opens a share.
          </div>
        </div>
      `;
    }
    return html`
      ${this.renderOverview()}
      ${!this.hasModel ? this.renderFullPreview() : nothing}
      ${this.renderSlides()}
    `;
  }

  private renderOverview(): TemplateResult {
    const a = this.analytics;
    const overview = toOverview(a);
    const viewerRows = toViewerRows(a?.recentViewers, this.totalSlides);
    return html`
      <div class="card">
        <div class="overview-stats">
          <div class="stat">
            <span class="num">${overview.totalOpens}</span>
            <span class="lbl">Total opens</span>
          </div>
          <div class="stat">
            <span class="num">${overview.uniqueViewers}</span>
            <span class="lbl">Unique viewers</span>
          </div>
          <div class="stat">
            <span class="num">${viewerRows.length}</span>
            <span class="lbl">Recent viewers</span>
          </div>
        </div>
        ${viewerRows.length > 0
          ? html`
              <div class="section-title">Recent viewers</div>
              <ul class="viewers">
                ${viewerRows.map((v, i) => {
                  const lastSeen = a?.recentViewers?.[i]?.lastSeen;
                  return html`
                    <li>
                      <span class="v-name">${v.primary}</span>
                      <span class="v-meta">
                        ${v.opens} open${v.opens === 1 ? '' : 's'}${v.progress
                          ? html` · ${v.progress}`
                          : nothing}${lastSeen
                          ? html` · ${fmtDate(lastSeen)}`
                          : nothing}
                      </span>
                    </li>
                  `;
                })}
              </ul>
            `
          : nothing}
      </div>
    `;
  }

  /** Single full-deck iframe for the no-model (legacy/raw-html) case. */
  private renderFullPreview(): TemplateResult {
    const deck = this.deck;
    if (!deck) return html``;
    return html`
      <div class="full-preview">
        <iframe
          class="preview-iframe"
          title=${deck.title}
          srcdoc=${deck.html}
          sandbox="allow-scripts allow-popups allow-forms allow-modals"
          allow="fullscreen"
          @load=${(e: Event) => this.onIframeLoad(e, true)}
        ></iframe>
      </div>
    `;
  }

  private renderSlides(): TemplateResult {
    const slideRows = toSlideRows(this.analytics?.perSlide);
    if (slideRows.length === 0) {
      return html`
        <div class="section-title">Slide views</div>
        <div class="empty">No per-slide data yet.</div>
      `;
    }
    const hasModel = this.hasModel;
    return html`
      <div class="section-title">Slide views</div>
      <div class="slides">
        ${slideRows.map((row) => this.renderSlideRow(row, hasModel))}
      </div>
    `;
  }

  private renderSlideRow(row: SlideRow, hasModel: boolean): TemplateResult {
    const label = this.slideLabel(row);
    const srcdoc = hasModel ? this.slideSrcdoc(row.slideIndex) : null;
    return html`
      <div class="slide-row ${hasModel ? '' : 'no-thumb'}">
        ${hasModel
          ? html`
              <div class="thumb">
                ${srcdoc
                  ? html`
                      <iframe
                        class="preview-iframe"
                        title="Slide ${row.label} preview"
                        srcdoc=${srcdoc}
                        sandbox="allow-scripts allow-popups allow-forms allow-modals"
                        allow="fullscreen"
                        @load=${(e: Event) => this.onIframeLoad(e, false)}
                      ></iframe>
                    `
                  : nothing}
              </div>
            `
          : nothing}
        <div class="slide-meta">
          <span class="slide-name">
            Slide ${row.label}${label ? html` <span class="slide-label">· ${label}</span>` : nothing}
          </span>
          <span class="bar-track">
            <span class="bar-fill" style="width:${row.barPct}%"></span>
          </span>
        </div>
        <span class="views">${row.views}</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'deck-analytics-page': DeckAnalyticsPage;
  }
}
